import type { OnboardingSessionConfig } from '@onefootprint/request-types';
import { Shimmer } from '@onefootprint/ui';
import { cx } from 'class-variance-authority';
import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import SecuredByFootprint from '../../secured-by-footprint';
import LanguageSelect from './language-select';
import SupportLinksSelect from './support-links-select';

type FootprintFooterProps = {
  config?: OnboardingSessionConfig;
  className?: string;
  hideSoc?: boolean;
  id?: string;
};

const Footer = forwardRef<HTMLDivElement, FootprintFooterProps>(({ config, className, hideSoc, id }, ref) => {
  const { t } = useTranslation('idv', { keyPrefix: 'global.components.footer' });
  const shouldShowSupportLinks = config?.supportEmail || config?.supportPhone || config?.supportWebsite;
  const links = [
    {
      label: t($ => $.links.privacy),
      href: config?.privacyPolicyUrl ?? 'https://onefootprint.com/privacy-policy',
    },
    {
      label: t($ => $.links.terms),
      href: config?.termsOfServiceUrl ?? 'https://onefootprint.com/terms-of-service',
    },
  ];

  return config ? (
    <footer
      id={id}
      ref={ref}
      className={cx(
        `flex items-center justify-between px-4 h-10`,
        {
          'justify-center': !config.showFootprintLogo,
          'justify-between': config.showFootprintLogo,
        },
        className,
      )}
    >
      {config.showFootprintLogo && <SecuredByFootprint />}
      <div className="flex items-center justify-center gap-3">
        {shouldShowSupportLinks && <SupportLinksSelect config={config} />}
        {links.map(({ href, label }) => (
          <a
            className="no-underline text-secondary hover:underline hover:underline-offset-2 hover:decoration-2 inline-block text-caption-1"
            href={href}
            key={label}
            rel="noreferrer"
            target="_blank"
          >
            {label}
          </a>
        ))}
        <LanguageSelect />
        {hideSoc ? null : (
          <div>
            <img src="/footer/soc-2-badge.png" height={32} width={32} alt="Soc2 badge" />
          </div>
        )}
      </div>
    </footer>
  ) : (
    <Shimmer height="50px" width="100%" />
  );
});

export default Footer;
